/**
 * Source Console - Scrolling intelligence feed during replay.
 * Shows Telegram posts, FIRMS events, and ADS-B contacts as they appear on the timeline.
 */
import React, { useEffect, useRef } from 'react';
import { Radio, Flame, Plane, Target } from 'lucide-react';

const FLAG_CODES = {
  'United States': 'us', 'Iran': 'ir', 'Israel': 'il', 'France': 'fr',
  'Russia': 'ru', 'Turkey': 'tr', 'Saudi Arabia': 'sa', 'UAE': 'ae',
  'United Kingdom': 'gb', 'Germany': 'de',
};

function FlagImg({ origin, size = 14 }) {
  const code = FLAG_CODES[origin];
  if (!code) return null;
  return <img src={`https://flagcdn.com/w20/${code}.png`} alt={origin} style={{ width: size, height: size * 0.7, borderRadius: 1, display: 'inline-block', verticalAlign: 'middle' }} />;
}

function formatTime(isoStr) {
  try {
    return new Date(isoStr).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return '--:--'; }
}

export default function SourceConsole({ events, isFullscreen }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events?.length]);

  return (
    <div
      className={`flex flex-col bg-black/95 border-l border-emerald-500/20 ${isFullscreen ? 'w-96' : 'w-80'}`}
      data-testid="source-console"
    >
      <div className="px-3 py-2 border-b border-emerald-500/30 flex items-center gap-2 flex-shrink-0 bg-emerald-900/20">
        <Radio className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
        <span className="font-mono text-emerald-400 text-xs uppercase tracking-widest font-bold">
          Sources en direct
        </span>
        <span className="ml-auto font-mono text-white/30 text-xs">{events?.length || 0}</span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto" style={{ scrollBehavior: 'smooth' }}>
        {(!events || events.length === 0) && (
          <div className="p-4 text-center text-white/30 font-mono text-xs">
            En attente du replay...
          </div>
        )}
        {events?.map((ev, i) => (
          <div
            key={`evt-${i}`}
            className="px-3 py-2 border-b border-white/5 animate-fadeIn"
            style={{ animationDelay: `${i * 30}ms` }}
          >
            {ev.type === 'telegram' && (
              <>
                <div className="flex items-center gap-1.5 mb-1">
                  <Radio className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                  <span className="font-mono text-xs font-bold" style={{ color: ev.color || '#00ff88' }}>
                    {ev.channel_label || ev.channel}
                  </span>
                  <span className="ml-auto font-mono text-white/25 text-xs">{formatTime(ev.date)}</span>
                </div>
                <p className="font-mono text-white/70 text-xs leading-relaxed line-clamp-3 pl-4">{ev.text}</p>
              </>
            )}
            {ev.type === 'explosion' && (
              <div className="flex items-start gap-2">
                <Target className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-red-400 text-xs font-bold uppercase">Frappe detectee</span>
                    {ev.attribution?.origin && <FlagImg origin={ev.attribution.origin} />}
                    <span className="ml-auto font-mono text-white/25 text-xs">{formatTime(ev.datetime)}</span>
                  </div>
                  <p className="font-mono text-red-300/70 text-xs mt-0.5">
                    FRP: {ev.frp} MW | {ev.lat?.toFixed(3)}, {ev.lon?.toFixed(3)}
                    {ev.attribution?.origin && (
                      <span className="text-red-400"> — Attribuee: {ev.attribution.origin} ({ev.attribution.type})</span>
                    )}
                    {!ev.attribution?.origin && <span className="text-yellow-500"> — Non attribuee</span>}
                  </p>
                </div>
              </div>
            )}
            {ev.type === 'firms' && (
              <div className="flex items-center gap-2">
                <Flame className="w-3 h-3 text-orange-400 flex-shrink-0" />
                <span className="font-mono text-orange-300/70 text-xs">
                  Detection thermique ({ev.intensity}) — FRP: {ev.frp} MW
                </span>
                <span className="ml-auto font-mono text-white/25 text-xs">{formatTime(ev.datetime)}</span>
              </div>
            )}
            {ev.type === 'aircraft' && (
              <div className="flex items-center gap-2">
                <Plane className="w-3 h-3 text-sky-400 flex-shrink-0" />
                <span className="font-mono text-sky-300/70 text-xs">
                  {ev.callsign || ev.icao24} — {ev.acType}
                </span>
                {ev.origin && ev.origin !== 'Unknown' && <FlagImg origin={ev.origin} size={12} />}
                <span className="ml-auto font-mono text-white/25 text-xs">{formatTime(ev.timestamp)}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
